/**
 * Response Comparison View - CR-1403
 *
 * Side-by-side comparison of supplier responses:
 * - Line item price comparison
 * - Highlight best prices
 * - Total cost comparison
 * - Award button per supplier
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, TrendingDown, Download } from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import type { RFQ, RFQComparison, SupplierSummary, LineComparison, SupplierResponseListItem } from '../../lib/api';
import AwardDialog from './AwardDialog';
import InvitedSuppliersList from './InvitedSuppliersList';
import ResponseSupplierCard from './ResponseSupplierCard';
import RequestRevisionDialog from './RequestRevisionDialog';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useRfqMoney } from './useRfqMoney';
import { toNumber } from './rfqFormat';

interface ResponseComparisonProps {
  rfq: RFQ;
  /** Invited suppliers (one response row each, any status) — drives the
   *  "awaiting response" list so a sent RFQ with no quotes yet still shows
   *  who was invited. */
  responses?: SupplierResponseListItem[];
  onAward?: () => void;
  /** A response changed server-side (certificate reviewed, revision requested) — refetch. */
  onChanged?: () => void;
}

export default function ResponseComparison({ rfq, responses = [], onAward, onChanged }: ResponseComparisonProps) {
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState<RFQComparison | null>(null);
  const [showAwardDialog, setShowAwardDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingCertId, setDownloadingCertId] = useState<number | null>(null);
  const [revisionTarget, setRevisionTarget] = useState<{ supplier: SupplierSummary; responseId: number } | null>(null);

  const handleChanged = () => {
    fetchComparison();
    onChanged?.();
  };

  const handleDownloadCertificate = async (id: number, fileName: string) => {
    try {
      setDownloadingCertId(id);
      const res = await rfqAPI.downloadCertificateSubmission(id);
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'certificate';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    } finally {
      setDownloadingCertId(null);
    }
  };

  // Buyers redact the comparison into Excel to circulate internally; the
  // on-screen table doesn't paste cleanly. Same data as this view.
  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await rfqAPI.exportComparison(rfq.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rfq.rfq_number}-responses.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error('Error exporting comparison:', error);
      toast.error('Failed to export the comparison');
    } finally {
      setExporting(false);
    }
  };
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierSummary | null>(null);

  useEffect(() => {
    fetchComparison();
  }, [rfq.id]);

  const fetchComparison = async () => {
    try {
      setLoading(true);
      const response = await rfqAPI.compareResponses(rfq.id);
      setComparison(response.data);
    } catch (error) {
      console.error('Error fetching comparison:', error);
      toast.error('Failed to load response comparison');
    } finally {
      setLoading(false);
    }
  };

  const handleAwardClick = (supplier: SupplierSummary) => {
    setSelectedSupplier(supplier);
    setShowAwardDialog(true);
  };

  const handleAwardComplete = () => {
    setShowAwardDialog(false);
    setSelectedSupplier(null);
    onAward?.();
  };

  const { formatDate } = useFormatters();
  const formatMoney = useRfqMoney(rfq.currency);
  const formatCurrency = (amount: string | number) => formatMoney(amount);

  const getBestPrice = (lineComparison: LineComparison): number | null => {
    const prices = lineComparison.supplier_quotes
      .map(q => toNumber(q.unit_price))
      .filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  const getLowestTotalSupplier = (): number | null => {
    if (!comparison?.suppliers.length) return null;
    let lowest = comparison.suppliers[0];
    for (const supplier of comparison.suppliers) {
      if (toNumber(supplier.grand_total) < toNumber(lowest.grand_total)) {
        lowest = supplier;
      }
    }
    return lowest.id;
  };

  const awaiting = responses.filter(r => r.response_status === 'pending' || r.response_status === 'in_progress');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!comparison || comparison.suppliers.length === 0) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">No supplier responses to compare yet.</p>
            {rfq.status === 'draft' && (
              <p className="text-sm text-muted-foreground mt-2">Send the RFQ to suppliers to start receiving responses.</p>
            )}
          </CardContent>
        </Card>
        {responses.length > 0 && <InvitedSuppliersList responses={responses} formatDate={(d) => formatDate(d)} />}
      </div>
    );
  }

  const lowestTotalSupplierId = getLowestTotalSupplier();

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {comparison.suppliers.map((supplier) => (
          <ResponseSupplierCard
            key={supplier.id}
            rfq={rfq}
            supplier={supplier}
            response={responses.find((r) => r.supplier === supplier.id)}
            isLowest={supplier.id === lowestTotalSupplierId}
            scoreWeights={comparison.score_weights}
            formatCurrency={formatCurrency}
            formatDate={(d) => formatDate(d)}
            onAward={handleAwardClick}
            onRequestRevision={(sup, responseId) => setRevisionTarget({ supplier: sup, responseId })}
            onDownloadCertificate={handleDownloadCertificate}
            downloadingCertId={downloadingCertId}
            onChanged={handleChanged}
          />
        ))}
      </div>

      {awaiting.length > 0 && (
        <InvitedSuppliersList
          responses={awaiting}
          title="Awaiting Response"
          description="Invited suppliers who have not submitted a quote yet"
          formatDate={(d) => formatDate(d)}
        />
      )}

      {/* Line Item Comparison Table */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Line Item Comparison</CardTitle>
            <CardDescription>Unit prices by line item across all suppliers</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="gap-2 flex-shrink-0">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export to Excel
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Line</th>
                  <th className="text-left p-3 font-medium">Description</th>
                  <th className="text-right p-3 font-medium">Qty</th>
                  {comparison.suppliers.map(supplier => (
                    <th key={supplier.id} className="text-right p-3 font-medium min-w-[120px]">
                      {supplier.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.line_comparisons.map((line) => {
                  const bestPrice = getBestPrice(line);
                  return (
                    <tr key={line.line_number} className="border-b hover:bg-muted/50">
                      <td className="p-3">{line.line_number}</td>
                      <td className="p-3">
                        <div className="font-medium">{line.item_description}</div>
                        <div className="text-xs text-muted-foreground">{line.part_number}</div>
                      </td>
                      <td className="p-3 text-right">
                        {toNumber(line.quantity).toLocaleString()} {line.unit_of_measure}
                      </td>
                      {comparison.suppliers.map(supplier => {
                        const quote = line.supplier_quotes.find(q => q.supplier_id === supplier.id);
                        const unitPrice = quote ? toNumber(quote.unit_price) : 0;
                        const isBest = unitPrice > 0 && unitPrice === bestPrice;

                        return (
                          <td key={supplier.id} className="p-3 text-right">
                            {quote && unitPrice > 0 ? (
                              <div className={isBest ? 'font-bold text-green-600' : ''}>
                                <div className="flex items-center justify-end gap-1">
                                  {isBest && <TrendingDown className="h-3 w-3" />}
                                  {formatCurrency(quote.unit_price)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Total: {formatCurrency(quote.total_price)}
                                </div>
                                {quote.lead_time_days && (
                                  <div className="text-xs text-muted-foreground">
                                    {quote.lead_time_days} days
                                  </div>
                                )}
                                {quote.uom_mismatch && (
                                  <div
                                    className="text-xs text-amber-700 font-medium"
                                    title={`Priced per ${quote.quoted_uom}, but the RFQ asks for ${line.unit_of_measure}. Check before comparing.`}
                                  >
                                    per {quote.quoted_uom}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/50 font-medium">
                <tr>
                  <td colSpan={3} className="p-3 text-right">Line Items Total:</td>
                  {comparison.suppliers.map(supplier => (
                    <td key={supplier.id} className="p-3 text-right">
                      {formatCurrency(supplier.total_quoted)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td colSpan={3} className="p-3 text-right">Additional Costs:</td>
                  {comparison.suppliers.map(supplier => (
                    <td key={supplier.id} className="p-3 text-right">
                      {formatCurrency(supplier.additional_costs)}
                    </td>
                  ))}
                </tr>
                <tr className="text-lg">
                  <td colSpan={3} className="p-3 text-right font-bold">Grand Total:</td>
                  {comparison.suppliers.map(supplier => {
                    const isLowest = supplier.id === lowestTotalSupplierId;
                    return (
                      <td
                        key={supplier.id}
                        className={`p-3 text-right font-bold ${isLowest ? 'text-green-600' : ''}`}
                      >
                        {formatCurrency(supplier.grand_total)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Additional Costs Breakdown */}
      {Object.keys(comparison.additional_cost_comparison).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Costs Breakdown</CardTitle>
            <CardDescription>Comparison of additional costs by type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Cost Type</th>
                    {comparison.suppliers.map(supplier => (
                      <th key={supplier.id} className="text-right p-3 font-medium">
                        {supplier.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(comparison.additional_cost_comparison).map(([costType, supplierCosts]) => (
                    <tr key={costType} className="border-b hover:bg-muted/50">
                      <td className="p-3 capitalize">{costType.replace('_', ' ')}</td>
                      {comparison.suppliers.map(supplier => (
                        <td key={supplier.id} className="p-3 text-right">
                          {supplierCosts[supplier.id]
                            ? formatCurrency(supplierCosts[supplier.id])
                            : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {revisionTarget && (
        <RequestRevisionDialog
          responseId={revisionTarget.responseId}
          supplier={revisionTarget.supplier}
          currency={rfq.currency}
          currentTotal={formatCurrency(revisionTarget.supplier.grand_total)}
          onClose={() => setRevisionTarget(null)}
          onRequested={handleChanged}
        />
      )}

      {/* Award Dialog */}
      {showAwardDialog && selectedSupplier && (
        <AwardDialog
          rfq={rfq}
          supplier={selectedSupplier}
          onClose={() => setShowAwardDialog(false)}
          onAward={handleAwardComplete}
        />
      )}
    </div>
  );
}
